import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ActionSheet, ActionSheetHeader, renderActionSheetItems } from "@air/primitive-action-sheet";
import { DropdownMenu, renderDropdownItems } from "@air/primitive-dropdown-menu";
import { useBreakpointsContext } from "@air/provider-media-query";
import { useAirModal } from "@air/provider-modal";
import noop from "lodash/noop";
import Router from "next/router";
import { cloneElement, useEffect } from "react";
/*
  this method can be used to manually hide menu dropdown
  useful e.g. when you have button in menu and you want to hide it after click
  it's hacky, but I didn't find any other way to hide it. It simulates mouseDown and mouseUp events on document body
  it can't be just 'click', since dropdown is hidden on mousedown event
  mouseup is required, because otherwise mouse scroll is fired
 */ export const manuallyHideDropdown = ()=>{
    const mouseDownEvt = new MouseEvent("mousedown", {
        view: window,
        bubbles: true
    });
    const mouseUpEvt = new MouseEvent("mouseup", {
        view: window,
        bubbles: true
    });
    document.body.dispatchEvent(mouseDownEvt);
    document.body.dispatchEvent(mouseUpEvt);
};
export const DropdownMenuActionSheet = (param)=>{
    let { content, isTitleHidden, onClose = noop, onChange = noop, title, options, ...restOfProps } = param;
    const { isAboveMediumScreen } = useBreakpointsContext();
    useEffect(()=>{
        if (isAboveMediumScreen) {
            onClose();
        }
    }, [
        isAboveMediumScreen,
        onClose
    ]);
    return /*#__PURE__*/ _jsxs(ActionSheet, {
        ...restOfProps,
        onClose: ()=>{
            onClose();
            onChange === null || onChange === void 0 ? void 0 : onChange(false);
        },
        children: [
            !isTitleHidden ? /*#__PURE__*/ _jsx(ActionSheetHeader, {
                onClose: ()=>{
                    onClose();
                    onChange === null || onChange === void 0 ? void 0 : onChange(false);
                },
                children: title
            }) : null,
            content,
            renderActionSheetItems({
                options
            })
        ]
    });
};
/**
 * This component conditional renders ActionSheet and DropdownMenu based
 * on the current screen size and should be only be used for menus that
 * renders on mobile. Otherwise, use `DropdownMenu` from `@air/zephyr`.
 */ export const DropdownMenuWithActionSheet = (param)=>{
    let { children, ["data-testid"]: dataTestId, isTitleHidden, onChange = noop, options, size, title, trigger, container, ["data-theme"]: dataTheme, dropdownClassName } = param;
    const [showActionSheet, close] = useAirModal(DropdownMenuActionSheet);
    const { isAboveMediumScreen } = useBreakpointsContext();
    useEffect(()=>{
        const closeActionSheet = ()=>{
            close();
            onChange(false);
        };
        Router.events.on("routeChangeComplete", closeActionSheet);
        return ()=>Router.events.off("routeChangeComplete", closeActionSheet);
    }, [
        close,
        onChange
    ]);
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: isAboveMediumScreen || container ? /*#__PURE__*/ _jsxs(DropdownMenu, {
            container: container,
            className: dropdownClassName,
            "data-testid": dataTestId,
            onOpenChange: onChange,
            size: size,
            trigger: trigger,
            "data-theme": dataTheme,
            children: [
                children,
                renderDropdownItems({
                    options
                })
            ]
        }) : /*#__PURE__*/ cloneElement(trigger, {
            onClick: (event)=>{
                event.stopPropagation();
                showActionSheet({
                    options: options === null || options === void 0 ? void 0 : options.map((option)=>{
                        if (option.type === "item") {
                            return {
                                ...option,
                                onSelect: ()=>{
                                    var _option_onSelect;
                                    close();
                                    (_option_onSelect = option.onSelect) === null || _option_onSelect === void 0 ? void 0 : _option_onSelect.call(option);
                                }
                            };
                        }
                        return option;
                    }),
                    isTitleHidden,
                    content: children,
                    onChange,
                    title
                });
                onChange(true);
            }
        })
    });
};
